import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, X, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";

const DemoCallForSolutionsBountyDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [isBounty, setIsBounty] = useState<string | null>(null);
  const [isAreaExpanded, setIsAreaExpanded] = useState(false);
  const [isBountyExpanded, setIsBountyExpanded] = useState(false);
  const [isBountyDetailsExpanded, setIsBountyDetailsExpanded] = useState(false);
  
  const [bountyDetails, setBountyDetails] = useState<any>(null);
  
  const [callTitle, setCallTitle] = useState("CURT Sprint Team for 2025 OMB Modernization");
  const [requirements, setRequirements] = useState(
    "Objective: Develop an updated, AI-consumable and blockchain-protected version of the Owners Modularization Blueprint (OMB) tailored for 2025 construction realities. The goal is to integrate industry best practices, smart contract governance, and digital twin-informed execution planning to improve capital project performance using the iSPAI model.\n\nSprint Team Structure: Duration: 8 weeks (Target: 2 months)"
  );
  const [additionalMaterials, setAdditionalMaterials] = useState(
    "https://curt.org/wp-content/uploads/2017/05/R-807-CURT-Owner-Safety-Blueprint-2017-Update.pdf"
  );
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  
  // Update these to preassigned values instead of "open"
  const [curatorAssignment, setCuratorAssignment] = useState<string>("assign");
  const [contributorAssignment, setContributorAssignment] = useState<string>("assign");
  const [confirmerAssignment, setConfirmerAssignment] = useState<string>("assign");
  const [composerAssignment, setComposerAssignment] = useState<string>("assign");
  
  // Update with preassigned team members
  const [curatorTeamMember, setCuratorTeamMember] = useState<string>("Karen Watson");
  const [contributorTeamMember, setContributorTeamMember] = useState<string>("Corey Vickers");
  const [confirmerTeamMember, setConfirmerTeamMember] = useState<string>("Giblet");
  const [composerTeamMember, setComposerTeamMember] = useState<string>("Trey Fleming");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("trey.fleming@ispai.io");
  const [message, setMessage] = useState(
    `Let's talk before you send this out.\n\nSprint Type: CURT-requested, Member Volunteer-led, iSPAI-powered\n\nParticipants:\n\nCreators (2): Guide Blueprint modernization structure\n\nContributors (4-6): Provide specialized input from domains like Engineering, Procurement, Logistics, AI/Data, and Modular Fabrication\n\nConfirmers (2): Validate contributions for completeness, compliance, and rigor\n\nComposers (1-2): Synthesize final OMB solution for enterprise use\n\nConsumer (1+): CURT Leadership + Owner reps`
  );

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const teamMembers = [
    "Trey Fleming",
    "Chris Johnson",
    "Miguel Santos",
    "Karen Lee",
    "Jim Patterson",
    "Morgan Williams"
  ];

  useEffect(() => {
    if (location.state) {
      setSelectedArea(location.state.selectedArea || "");
      setIsBounty(location.state.isBounty || null);
      
      if (location.state.bountyDetails) {
        setBountyDetails(location.state.bountyDetails);
      }
    }
  }, [location]);

  const handleBack = () => {
    navigate("/demo-call-for-solutions/bounty", { state: { selectedArea } });
  };

  const handleContinue = () => {
    console.log("Submitting detailed requirements:", {
      selectedArea,
      isBounty,
      bountyDetails,
      callTitle,
      requirements,
      additionalMaterials,
      selectedFiles
    });
  };

  const handleSendMessage = () => {
    setShowConfirmationDialog(true);
  };
  
  const handleConfirmSend = () => {
    console.log("Demo - Sending message to curator:", { email, message });
    
    const updatedBountyDetails = {
      ...bountyDetails,
      roleAssignments: {
        curator: {
          status: curatorAssignment,
          assignedTo: curatorAssignment === "assign" ? curatorTeamMember : "Open"
        },
        contributor: {
          status: contributorAssignment,
          assignedTo: contributorAssignment === "assign" ? contributorTeamMember : "Open"
        },
        confirmer: {
          status: confirmerAssignment,
          assignedTo: confirmerAssignment === "assign" ? confirmerTeamMember : "Open"
        },
        composer: {
          status: composerAssignment,
          assignedTo: composerAssignment === "assign" ? composerTeamMember : "Open"
        }
      }
    };
    
    setShowConfirmationDialog(false);
    
    setEmail("");
    setMessage("");
    
    toast({
      title: "Message Sent Successfully",
      description: "Your Demo Call for Solutions has been submitted and will appear in your mySPAI profile.",
    });
    
    navigate("/demo-creator-rewards", { 
      state: { 
        newCall: {
          title: callTitle,
          area: selectedArea,
          type: isBounty === "yes" ? "Demo Solution Bounty" : "Demo Call for Solutions",
          date: new Date().toLocaleDateString(),
          bountyDetails: updatedBountyDetails
        }
      }
    });
  };

  const handleFileButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const totalFiles = [...selectedFiles, ...newFiles];
      
      if (totalFiles.length > 5) {
        toast({
          title: "Maximum Files Exceeded",
          description: "You can upload a maximum of 5 files.",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedFiles(totalFiles);
    }
  };
  
  const removeFile = (index: number) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
  };
  
  const generateEmailPreview = () => {
    return (
      <div className="space-y-4 text-sm">
        <div>
          <p><strong>To:</strong> Creator</p>
          <p><strong>From:</strong> {email}</p>
          <p><strong>Subject:</strong> New Call for Solutions: {callTitle}</p>
        </div>
        
        <div className="border-t pt-2">
          <p className="mb-2"><strong>Call Details:</strong></p>
          <p><strong>Title:</strong> {callTitle}</p>
          <p><strong>Functional Area:</strong> {selectedArea}</p>
          <p><strong>Type:</strong> {isBounty === "yes" ? "Demo Solution Bounty" : "Demo Call for Solutions"}</p>
          
          {isBounty === "yes" && bountyDetails && (
            <div className="mt-2">
              <p><strong>Bounty Amount:</strong> {bountyDetails.bountyAmount || "Not specified"}</p>
              <p><strong>Bounty Type:</strong> {bountyDetails.bountyType || "Not specified"}</p>
              
              {bountyDetails.attribution && (
                <div className="mt-2">
                  <p><strong>Attribution and Role Assignments:</strong></p>
                  <ul className="list-disc pl-5 mt-1">
                    <li>Creator ({bountyDetails.attribution.curatorPercentage || 0}%): {curatorTeamMember}</li>
                    <li>Contributor ({bountyDetails.attribution.contributorPercentage || 0}%): {contributorTeamMember}</li>
                    <li>Confirmer ({bountyDetails.attribution.confirmerPercentage || 0}%): {confirmerTeamMember} {confirmerTeamMember === "Giblet" && <span className="inline-flex items-center text-blue-500"><Lock size={12} className="mr-1" /> Powered by Giblet</span>}</li>
                    <li>Composer ({bountyDetails.attribution.composerPercentage || 0}%): {composerTeamMember}</li>
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <div className="mt-2">
            <p><strong>Requirements:</strong></p>
            <p className="whitespace-pre-wrap">{requirements}</p>
          </div>
          
          {additionalMaterials && (
            <div className="mt-2">
              <p><strong>Additional Materials/Links:</strong></p>
              <p className="whitespace-pre-wrap">{additionalMaterials}</p>
            </div>
          )}
          
          {selectedFiles.length > 0 && (
            <div className="mt-2">
              <p><strong>Attached Files:</strong></p>
              <ul className="list-disc pl-5">
                {selectedFiles.map((file, index) => (
                  <li key={index}>{file.name}</li>
                ))}
              </ul>
            </div>
          )}
          
          <div className="mt-4 pt-2 border-t">
            <p><strong>Message from submitter:</strong></p>
            <p className="whitespace-pre-wrap">{message}</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/engage")}
        />
      </div>

      <div className="w-full max-w-2xl space-y-6">
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsAreaExpanded(!isAreaExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">Select Functional Area</h2>
              <p className="text-gray-600 mt-1">Selected: {selectedArea}</p>
            </div>
            <div>
              {isAreaExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsBountyExpanded(!isBountyExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">Solution Bounty</h2>
              <p className="text-gray-600 mt-1">Is this a solution bounty? {isBounty === "yes" ? "Yes" : "No"}</p>
            </div>
            <div>
              {isBountyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        {bountyDetails && (
          <Card className="border border-gray-200 overflow-hidden">
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => setIsBountyDetailsExpanded(!isBountyDetailsExpanded)}
            >
              <div>
                <h2 className="text-xl font-semibold">Solution Bounty Details</h2>
                <p className="text-gray-600 mt-1">
                  Amount: {bountyDetails.bountyAmount || "Not specified"}, 
                  Type: {bountyDetails.bountyType || "Not specified"}
                </p>
              </div>
              <div>
                {isBountyDetailsExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </div>
            
            {isBountyDetailsExpanded && (
              <div className="p-4 border-t border-gray-200">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Escrow Conditions</h3>
                    <p className="text-sm text-gray-600 mt-1">{bountyDetails.escrowConditions || "Not specified"}</p>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium text-gray-700">Release Conditions</h3>
                    <p className="text-sm text-gray-600 mt-1">{bountyDetails.releaseConditions || "Not specified"}</p>
                  </div>
                  
                  {bountyDetails.attribution && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700">Attribution and Role Assignments</h3>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <p className="text-sm text-gray-600">Creator: {bountyDetails.attribution.curatorPercentage || 0}%</p>
                        <p className="text-sm text-gray-600">Contributor: {bountyDetails.attribution.contributorPercentage || 0}%</p>
                        <p className="text-sm text-gray-600">Confirmer: {bountyDetails.attribution.confirmerPercentage || 0}%</p>
                        <p className="text-sm text-gray-600">Composer: {bountyDetails.attribution.composerPercentage || 0}%</p>
                      </div>
                      
                      <div className="space-y-4 mt-3">
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700">Creator ({bountyDetails.attribution.curatorPercentage || 0}%)</p>
                          <div className="flex space-x-3">
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="curator-assign" 
                                name="curator-assignment" 
                                value="assign"
                                checked={curatorAssignment === "assign"}
                                onChange={() => setCuratorAssignment("assign")}
                                className="mr-2" 
                              />
                              <label htmlFor="curator-assign" className="text-sm">Assign</label>
                            </div>
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="curator-open" 
                                name="curator-assignment" 
                                value="open"
                                checked={curatorAssignment === "open"}
                                onChange={() => setCuratorAssignment("open")}
                                className="mr-2" 
                              />
                              <label htmlFor="curator-open" className="text-sm">Open Call</label>
                            </div>
                          </div>
                          
                          {curatorAssignment === "assign" && (
                            <Select onValueChange={setCuratorTeamMember} value={curatorTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent>
                                {teamMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700">Contributor ({bountyDetails.attribution.contributorPercentage || 0}%)</p>
                          <div className="flex space-x-3">
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="contributor-assign" 
                                name="contributor-assignment" 
                                value="assign"
                                checked={contributorAssignment === "assign"}
                                onChange={() => setContributorAssignment("assign")}
                                className="mr-2" 
                              />
                              <label htmlFor="contributor-assign" className="text-sm">Assign</label>
                            </div>
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="contributor-open" 
                                name="contributor-assignment" 
                                value="open"
                                checked={contributorAssignment === "open"}
                                onChange={() => setContributorAssignment("open")}
                                className="mr-2" 
                              />
                              <label htmlFor="contributor-open" className="text-sm">Open Call</label>
                            </div>
                          </div>
                          
                          {contributorAssignment === "assign" && (
                            <Select onValueChange={setContributorTeamMember} value={contributorTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent>
                                {teamMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                          
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700">Confirmer ({bountyDetails.attribution.confirmerPercentage || 0}%)</p>
                          <div className="flex space-x-3">
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="confirmer-assign" 
                                name="confirmer-assignment" 
                                value="assign"
                                checked={confirmerAssignment === "assign"}
                                onChange={() => setConfirmerAssignment("assign")}
                                className="mr-2" 
                              />
                              <label htmlFor="confirmer-assign" className="text-sm">Assign</label>
                            </div>
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="confirmer-open" 
                                name="confirmer-assignment" 
                                value="open"
                                checked={confirmerAssignment === "open"}
                                onChange={() => setConfirmerAssignment("open")}
                                className="mr-2" 
                              />
                              <label htmlFor="confirmer-open" className="text-sm">Open Call</label>
                            </div>
                          </div>
                          
                          {confirmerAssignment === "assign" && (
                            <Select onValueChange={setConfirmerTeamMember} value={confirmerTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent>
                                {teamMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                          
                        <div className="space-y-2">
                          <p className="text-sm text-gray-700">Composer ({bountyDetails.attribution.composerPercentage || 0}%)</p>
                          <div className="flex space-x-3">
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="composer-assign" 
                                name="composer-assignment" 
                                value="assign"
                                checked={composerAssignment === "assign"}
                                onChange={() => setComposerAssignment("assign")}
                                className="mr-2" 
                              />
                              <label htmlFor="composer-assign" className="text-sm">Assign</label>
                            </div>
                            <div className="flex items-center">
                              <input 
                                type="radio" 
                                id="composer-open" 
                                name="composer-assignment" 
                                value="open"
                                checked={composerAssignment === "open"}
                                onChange={() => setComposerAssignment("open")}
                                className="mr-2" 
                              />
                              <label htmlFor="composer-open" className="text-sm">Open Call</label>
                            </div>
                          </div>
                          
                          {composerAssignment === "assign" && (
                            <Select onValueChange={setComposerTeamMember} value={composerTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent>
                                {teamMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>
        )}

        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-2">Detailed Requirements</h2>
            <p className="text-gray-600 mb-4">Specify the requirements and provide any additional materials</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Call Title</label>
                <Input 
                  placeholder="Enter a title for this Call for Solutions..." 
                  value={callTitle}
                  onChange={(e) => setCallTitle(e.target.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
                <Textarea 
                  placeholder="Describe in detail what needs to be done..." 
                  className="min-h-[100px]"
                  value={requirements}
                  onChange={(e) => setRequirements(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Materials/Links</label>
                <Textarea 
                  placeholder="Add any relevant links or materials..." 
                  className="min-h-[100px]"
                  value={additionalMaterials}
                  onChange={(e) => setAdditionalMaterials(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Upload Files (Max 5)</label>
                <div className="flex items-center mt-1">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    multiple
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-sm"
                    onClick={handleFileButtonClick}
                    disabled={selectedFiles.length >= 5}
                  >
                    Choose Files
                  </Button>
                  <span className="ml-2 text-sm text-gray-500">
                    {selectedFiles.length > 0 
                      ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected` 
                      : "No files chosen"}
                  </span>
                </div>
                
                {selectedFiles.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Selected Files:</p>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                          <span className="text-sm truncate max-w-xs">{file.name}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0" 
                            onClick={() => removeFile(index)}
                          >
                            <X size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-2">Contact Curator</h2>
            <p className="text-gray-600 mb-4">Have questions? Send a message to the curator</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message to Curator</label>
                <Textarea 
                  placeholder="Enter your message or question for the curator..." 
                  className="min-h-[100px]"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>
              
              <Button 
                className="w-full bg-[#14213d] hover:bg-[#0d1829] text-white"
                onClick={handleSendMessage}
                disabled={!email || !message || !callTitle}
              >
                Send Message
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between mb-12">
          <Button 
            variant="outline" 
            onClick={handleBack}
            className="border-gray-300"
          >
            Back
          </Button>
          
          <Button 
            onClick={handleContinue}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
          >
            Continue
          </Button>
        </div>
      </div>
      
      <Dialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm Call for Solutions Submission</DialogTitle>
            <DialogDescription>
              The following message will be sent. After submission, this Call for Solutions will appear in your mySPAI profile.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4 border rounded-md p-4 bg-gray-50">
            {generateEmailPreview()}
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowConfirmationDialog(false)}>Cancel</Button>
            <Button 
              className="bg-[#14213d] hover:bg-[#0d1829] text-white"
              onClick={handleConfirmSend}
            >
              Confirm & Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DemoCallForSolutionsBountyDetails;
